import React, { Component } from "react"
import { connect } from "react-redux"
import { Router, Route, Switch, Link } from "react-router-dom"
import { createBrowserHistory } from "history"
import { ToastContainer, toast as toastShow } from "react-toastify"
import LoadingBar from "react-redux-loading-bar"
import Bowser from "bowser"
import _get from "lodash/get"

// font awesome
import { library } from "@fortawesome/fontawesome-svg-core"

import {
  faCaretUp,
  faCaretDown,
  faSearch,
  faCodeBranch,
  faWrench,
  faTrashAlt as solidTrashAlt,
  faChevronLeft as chevronLeftSolid,
  faPlay as faPlaySolid,
  faPause,
  faExclamation,
  faCheck,
  faHourglass,
  faTimes as faTimesSolid,
  faMinus,
  faArrowAltCircleDown,
  faArrowAltCircleUp,
  faCloud,
  faCode,
  faInfoCircle,
  faStream as faStreamSolid,
  faFileContract,
  faUser as faUserSolid,
  faLifeRing,
  faMicrochip,
  faCopy as faCopySolid,
  faArrowCircleUp,
  faBan,
  faExclamationTriangle,
  faEye as faEyeSolid,
  faPencil as faPencilSolid,
  faPowerOff as faPowerOffSolid,
  faQuestion as faQuestionSolid,
  faChevronRight as faChevronRightSolid,
  faChevronDown as faChevronDownSolid,
  faChevronUp as faChevronUpSolid,
  faLocation as faLocationSolid,
  faPlus as faPlusSolid,
  faUserEdit as faUserEditSolid,
  faRedo as faRedoSolid,
  faLevelUp as faLevelUpSolid,
  faFilter as faFilterSolid,
  faDownload as faDownloadSolid,
  faUndo as faUndoSolid,
  faTag as faTagSolid,
  faEraser as faEraserSolid,
  faUserPlus as faUserPlusSolid,
  faPlusCircle as faPlusCircleSolid,
  faSortAmountDown,
  faSortAlphaDown as faSortAlphaDownSolid,
  faSortAlphaUp as faSortAlphaUpSolid,
  faCog as faCogSolid,
  faSyncAlt as faSyncAltSolid,
  faCogs as faCogsSolid,
  faTerminal as faTerminalSolid,
  faDatabase as faDatabaseSolid,
  faCrown,
  faSignOut,
  faTachometerAlt,
  faNetworkWired,
  faSort,
  faMapMarkerAlt,
  faLock
} from "@fortawesome/free-solid-svg-icons"

import {
  faUser,
  faEnvelope,
  faTrashAlt as trashAltRegular,
  faClone,
  faPaperPlane,
  faEdit,
  faEyeSlash,
  faEye as faEyeRegular,
  faUserCircle
} from "@fortawesome/free-regular-svg-icons"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

// scroll to top
import ScrollToTop from "./ScrollToTop"

// auth helpers
import { userIsAuthenticatedRedir, userIsNotAuthenticatedRedir } from "auth"

// ui components
import Login from "components/Login"
import OktaCallback from "components/Login/oktaCallback"
import LogoutPage from "components/Page/LogoutPage"
import AuthenticatedLayout from "components/UI/AuthenticatedLayout"
import PasswordResetSection from "components/Password/pages/PasswordReset"
import PasswordSetSection from "components/Password/pages/PasswordSet"
import getUserConfirmation from "./getUserConfirmation"
import AppUpdateModal from "./AppUpdateModal"

// constants, helpers
import { TOAST } from "sharedConstants"
import { getRoutePath } from "routes"

import "./App.css"
import "react-toastify/dist/ReactToastify.css"

// own history instance
export const history = createBrowserHistory({
  getUserConfirmation: getUserConfirmation
})

const LoginSection = userIsNotAuthenticatedRedir(Login)
const OktaCallbackSection = userIsNotAuthenticatedRedir(OktaCallback)
const AuthenticatedSection = userIsAuthenticatedRedir(AuthenticatedLayout)

const createIconAlias = (iconDefinition, prefix, iconName) => ({
  ...iconDefinition,
  prefix,
  iconName
})

const faQuestion = createIconAlias(faQuestionSolid, "far", "question")
const faTimes = createIconAlias(faTimesSolid, "far", "times")
const faChevronRight = createIconAlias(faChevronRightSolid, "far", "chevron-right")
const faChevronLeft = createIconAlias(chevronLeftSolid, "far", "chevron-left")
const faChevronDown = createIconAlias(faChevronDownSolid, "far", "chevron-down")
const faChevronUp = createIconAlias(faChevronUpSolid, "far", "chevron-up")
const faLocation = createIconAlias(faLocationSolid, "far", "location")
const faPlay = createIconAlias(faPlaySolid, "far", "play")
const faPlus = createIconAlias(faPlusSolid, "far", "plus")
const faUserEdit = createIconAlias(faUserEditSolid, "far", "user-edit")
const faRedo = createIconAlias(faRedoSolid, "far", "redo")
const faPowerOffRegular = createIconAlias(faPowerOffSolid, "far", "power-off")
const faLevelUp = createIconAlias(faLevelUpSolid, "far", "level-up")
const faStream = createIconAlias(faStreamSolid, "far", "stream")
const faFilter = createIconAlias(faFilterSolid, "far", "filter")
const faDownload = createIconAlias(faDownloadSolid, "far", "download")
const faCogsRegular = createIconAlias(faCogsSolid, "far", "cogs")
const faUndo = createIconAlias(faUndoSolid, "far", "undo")
const faTag = createIconAlias(faTagSolid, "far", "tag")
const faEraser = createIconAlias(faEraserSolid, "far", "eraser")
const faUserPlus = createIconAlias(faUserPlusSolid, "far", "user-plus")
const faPlusCircleRegular = createIconAlias(faPlusCircleSolid, "far", "plus-circle")
const faSortSizeDownAlt = createIconAlias(faSortAmountDown, "far", "sort-size-down-alt")
const faSortAlphaDown = createIconAlias(faSortAlphaDownSolid, "far", "sort-alpha-down")
const faSortAlphaUp = createIconAlias(faSortAlphaUpSolid, "far", "sort-alpha-up")
const faCog = createIconAlias(faCogSolid, "far", "cog")
const faExclamationTriangleRegular = createIconAlias(
  faExclamationTriangle,
  "far",
  "exclamation-triangle"
)
const faSyncAlt = createIconAlias(faSyncAltSolid, "far", "sync-alt")
const faEye = createIconAlias(faEyeSolid, "fal", "eye")
const faPencil = createIconAlias(faPencilSolid, "fal", "pencil")
const faPowerOff = createIconAlias(faPowerOffSolid, "fal", "power-off")
const faCopy = createIconAlias(faCopySolid, "fal", "copy")
const faTrafficCone = createIconAlias(faExclamationTriangle, "fas", "traffic-cone")
const faTachometerFastest = createIconAlias(faTachometerAlt, "fas", "tachometer-fastest")
const faChartNetwork = createIconAlias(faNetworkWired, "fas", "chart-network")
const faSortAlt = createIconAlias(faSort, "fas", "sort-alt")
const faGearCode = createIconAlias(faCogsSolid, "fas", "gear-code")
const faBinary = createIconAlias(faCode, "fas", "binary")
const faLockKeyhole = createIconAlias(faLock, "fas", "lock-keyhole")

library.add(
  faEyeSolid,
  faPencilSolid,
  faCrown,
  faCaretUp,
  faCaretDown,
  faSignOut,
  faUser,
  faEnvelope,
  faQuestion,
  faSearch,
  faTimes,
  faTag,
  faChevronRight,
  faChevronLeft,
  faCodeBranch,
  faWrench,
  solidTrashAlt,
  chevronLeftSolid,
  faEye,
  faPencil,
  trashAltRegular,
  faLocation,
  faClone,
  faPowerOff,
  faPlay,
  faPlus,
  faPaperPlane,
  faUserEdit,
  faEdit,
  faRedo,
  faSyncAlt,
  faPlaySolid,
  faPause,
  faExclamation,
  faMinus,
  faCheck,
  faHourglass,
  faPowerOffRegular,
  faLevelUp,
  faStream,
  faFilter,
  faTrafficCone,
  faChevronUp,
  faChevronDown,
  faDownload,
  faTimesSolid,
  faCogsRegular,
  faUndo,
  faArrowAltCircleDown,
  faArrowAltCircleUp,
  faCopy,
  faEraser,
  faUserPlus,
  faCloud,
  faCode,
  faExclamationTriangle,
  faPlusCircleRegular,
  faInfoCircle,
  faEyeSlash,
  faEyeRegular,
  faStreamSolid,
  faFileContract,
  faUserSolid,
  faLifeRing,
  faTachometerFastest,
  faMicrochip,
  faCopySolid,
  faSortSizeDownAlt,
  faSortAlphaDown,
  faSortAlphaUp,
  faArrowCircleUp,
  faChartNetwork,
  faUserCircle,
  faSortAlt,
  faBan,
  faCog,
  faExclamationTriangleRegular,
  faCogsSolid,
  faTerminalSolid,
  faDatabaseSolid,
  faGearCode,
  faBinary,
  faMapMarkerAlt,
  faLockKeyhole
)

/*
 * Toast custom close button
 */
const CloseButton = ({ closeToast }) => (
  <span className="toast-close" onClick={closeToast}>
    <FontAwesomeIcon icon={["fas", "times"]} />
  </span>
)

const ToastContent = ({ text, route, closeToast, type }) => {
  const renderToastIcon = () => {
    let faIcon
    if (type === TOAST.TYPE.ERROR) {
      faIcon = "exclamation-triangle"
    } else if (type === TOAST.TYPE.SUCCESS) {
      faIcon = "check"
    } else {
      faIcon = "cogs"
    }
    return (
      <span className="toast-icon">
        <FontAwesomeIcon icon={["fas", faIcon]} />
      </span>
    )
  }

  if (route) {
    return (
      <React.Fragment>
        {renderToastIcon()}

        <span className="toast-text" data-cy="toast-text">
          {text}
          <Link
            to={{ pathname: route, state: { previous: true } }}
            className="link"
            data-cy="toast-link"
            onClick={closeToast}
          >
            View <FontAwesomeIcon icon={["far", "chevron-right"]} />
          </Link>
        </span>
      </React.Fragment>
    )
  } else {
    return (
      <React.Fragment>
        {renderToastIcon()}
        <span className="toast-text" data-cy="toast-text">
          {text}
        </span>
      </React.Fragment>
    )
  }
}

class App extends Component {
  constructor(props) {
    super(props)
    this.registration = false
    this.state = {
      isValidBrowser: true,
      invalidBrowserWarningClosed: false,
      newServiceWorkerDetected: false
    }

    window.addEventListener("blur", this.dismissAllToasts, false)
  }

  componentWillUnmount() {
    window.removeEventListener("blur", this.dismissAllToasts, false)
    document.removeEventListener("onNewServiceWorker", this.handleNewServiceWorker)
  }

  dismissAllToasts = () => {
    toastShow.dismiss()
  }

  componentDidMount() {
    const browser = Bowser.getParser(window.navigator.userAgent)
    const isValidBrowser = browser.satisfies({
      chrome: ">=58",
      edge: ">=16",
      firefox: ">=54",
      chromium: ">=58",
      safari: ">=10.1",
      opera: ">=44"
    })
    this.setState({
      isValidBrowser
    })
    document.addEventListener("onNewServiceWorker", this.handleNewServiceWorker)
  }

  componentDidUpdate(prevProps) {
    const { toast } = this.props
    if (toast.id !== prevProps.toast.id) {
      if (this.toastId && toast.message === prevProps.toast.message) {
        toastShow.dismiss(this.toastId)
      }
      this.toastId = this.notify(toast)
    }
  }

  notify = data => {
    const message = _get(data, "message", "")
    const wider = message.length > 120
    // display notification only if the app is visible
    switch (data.type) {
      case TOAST.TYPE.ERROR:
        return toastShow.error(<ToastContent text={message} type={data.type} />, {
          position: toastShow.POSITION.TOP_RIGHT,
          className: `toast toast-error ${wider ? "wide" : ""} ${data.route ? "with-route" : ""}`,
          bodyClassName: "toast-body",
          closeButton: <CloseButton />,
          autoClose: false,
          draggable: false,
          closeOnClick: false
        })
      case TOAST.TYPE.SUCCESS:
        return toastShow.success(
          <ToastContent text={message} route={data.route} type={data.type} />,
          {
            position: toastShow.POSITION.TOP_RIGHT,
            className: `toast toast-success ${wider ? "wide" : ""} ${
              data.route ? "with-route" : ""
            }`,
            bodyClassName: "toast-body",
            closeButton: <CloseButton />,
            pauseOnHover: true,
            pauseOnFocusLoss: false,
            autoClose: 5000,
            draggable: false,
            closeOnClick: false
          }
        )
      case TOAST.TYPE.INFO:
      default:
        return toastShow.info(<ToastContent text={message} route={data.route} type={data.type} />, {
          position: toastShow.POSITION.TOP_RIGHT,
          className: `toast toast-info ${wider ? "wide" : ""} ${data.route ? "with-route" : ""}`,
          bodyClassName: "toast-body",
          closeButton: <CloseButton />,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          autoClose: false,
          draggable: false,
          closeOnClick: false
        })
    }
  }

  closeInvalidBrowserWarning = () => {
    this.setState({
      invalidBrowserWarningClosed: true
    })
  }

  handleNewServiceWorker = evt => {
    this.registration = evt.detail.registration
    this.setState({
      newServiceWorkerDetected: true
    })
  }

  render() {
    const { isValidBrowser, invalidBrowserWarningClosed, newServiceWorkerDetected } = this.state
    const { onLoadNewServiceWorkerAccept } = this.props
    return (
      <Router getUserConfirmation={getUserConfirmation} history={history}>
        <ScrollToTop>
          <div className="app">
            <LoadingBar
              style={{
                top: "0px",
                zIndex: "10000",
                backgroundColor: "#ffffff",
                position: "fixed",
                boxShadow: "0 0 6px 2px rgba(250, 190, 83, 0.5)"
              }}
            />
            {!isValidBrowser && !invalidBrowserWarningClosed && (
              <div className="invalid-browser-message">
                <div className="wrapper">
                  <div className="left-part">
                    <FontAwesomeIcon
                      icon={["far", "exclamation-triangle"]}
                      className="exclamation-icon"
                    />
                    <p>
                      Bear in mind that you use old and unsupported browser. We recommend upgrading
                      to the latest{" "}
                      <a
                        href="https://www.apple.com/safari/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Safari
                      </a>
                      ,{" "}
                      <a
                        href="https://www.google.com/chrome/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Chrome
                      </a>{" "}
                      or{" "}
                      <a
                        href="https://www.mozilla.org/en-US/firefox/new/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Firefox
                      </a>
                      .
                    </p>
                  </div>
                  <div className="right-part">
                    <span className="warning-close" onClick={this.closeInvalidBrowserWarning}>
                      Dismiss
                    </span>
                  </div>
                </div>
              </div>
            )}
            <AppUpdateModal
              open={newServiceWorkerDetected}
              updateFn={onLoadNewServiceWorkerAccept(this.registration)}
              history={history}
            />
            <Switch>
              <Route path={getRoutePath("password.reset")} component={PasswordResetSection} />
              <Route path={getRoutePath("password.set")} component={PasswordSetSection} />
              <Route path={getRoutePath("login")} component={LoginSection} />
              <Route path={getRoutePath("okta.callback")} component={OktaCallbackSection} />
              <Route path={getRoutePath("logout")} component={LogoutPage} />
              <Route path={getRoutePath("dashboard")} component={AuthenticatedSection} />
            </Switch>
            <ToastContainer
              className="custom-toastify"
              autoClose={5000}
              hideProgressBar={true}
              pauseOnHover={true}
              pauseOnFocusLoss={false}
            />
          </div>
        </ScrollToTop>
      </Router>
    )
  }
}

function mapStateToProps({ toast }) {
  return { toast }
}

export default connect(mapStateToProps)(App)
